<template>
    <div class="panel-contents">
        <div class="d-flex justify-content-between">
            <div class="d-flex align-items-center">
                <UserAvatar
                    v-if="thread?.is_support_thread"
                    style="width: 3.5rem"
                    avatar-icon="fi-support"
                ></UserAvatar>
                <UserAvatarStack
                    v-else
                    :users="otherUsers"
                    style="width: 4rem"
                    :clickable="true"
                ></UserAvatarStack>
                <h3>{{ threadTitle }}</h3>
            </div>

            <KebabMenu
                :menu-items="menuItems"
                @thread-users="messengerStore.openThreadUsers(thread.id)"
                @leave-thread="leaveThread(thread.id)"
            />
        </div>

        <div class="thread-messages-container" ref="messagesContainer">
            <div
                v-for="msg in mutatedMessages"
                :key="msg.id"
                class="message-row"
                :class="{
                    'outgoing-message': msg.isMine,
                    'incoming-message': !msg.isMine,
                }"
            >
                <UserAvatar :user="msg.author" :clickable="true"></UserAvatar>

                <div class="message">
                    <div class="message-body">
                        <h5>
                            {{ msg.authorName }}
                            <span v-if="msg.isTechSupport" class="support-text"
                                >({{
                                    $t('messages.thread.supportLabel')
                                }})</span
                            >
                        </h5>
                        <div class="time-details">{{ msg.metaDetails }}</div>
                        <div class="text">{{ msg.message }}</div>
                    </div>
                </div>
            </div>

            <div
                v-if="exitedThread"
                class="text-center text-muted"
                style="font-size: 0.8rem"
            >
                {{ $t('messages.thread.leftThread') }}
            </div>

            <div
                v-else-if="showSupportPlaceholder"
                class="text-left text-muted"
                style="font-size: 0.8rem"
            >
                {{ $t('messages.thread.supportBusy') }}
            </div>
        </div>

        <hr />

        <MessageField
            :disabled="exitedThread"
            v-model="messageInput"
            @submit="submitMessage"
        ></MessageField>
    </div>
</template>

<script>
import UserAvatar from '../UserAvatar.vue';
import UserAvatarStack from '../UserAvatarStack.vue';
import MessageField from '@/components/fields/MessageField.vue';
import KebabMenu from '@/components/KebabMenu.vue';

import { useMessengerStore } from '@/stores/messenger';
import moment from 'moment';
import { formatLocalizedDateTime } from '@/utils/dateUtils';
import { getThreadUserDisplayUser as buildThreadUserDisplayUser } from '@/utils/messaging';

export default {
    name: 'MessageThread',
    components: {
        UserAvatar,
        UserAvatarStack,
        MessageField,
        KebabMenu,
    },
    props: {
        threadId: {
            type: Number,
            required: true,
        },
    },

    data() {
        return {
            thread: null,
            fetching: false,

            messageInput: '',

            threadSocket: null,
            messengerStore: null,

            intervalSupportCheck: null,
            showSupportPlaceholder: false,
        };
    },

    computed: {
        menuItems() {
            let items = [
                {
                    label: this.$t('messages.actions.viewMembers'),
                    click: 'thread-users',
                },
                {
                    label: this.$t('messages.actions.leave'),
                    click: 'leave-thread',
                },
            ];

            if (this.exitedThread) {
                items.splice(1, 1);
            }

            return items;
        },
        threadTitle() {
            return (
                this.thread?.subject || this.$t('messages.threadList.noSubject')
            );
        },
        exitedThread() {
            const threadUser = this.thread?.users.find((threadUser) => {
                return threadUser.user.id == this.$userStore.user.id;
            });
            return threadUser?.exited_at;
        },
        otherUsers() {
            if (!(this.thread && 'users' in this.thread && this.thread.users))
                return [];

            const otherUserRelations = this.thread.users.filter(
                (user) => user.user_id != this.$userStore.user.id
            );

            if (otherUserRelations.length) {
                return otherUserRelations.map((threadUser) => {
                    return this.getThreadUserDisplayUser(threadUser);
                });
            }

            return [
                buildThreadUserDisplayUser(
                    { user_id: this.$userStore.user.id },
                    this.$employeesStore
                ),
            ];
        },

        mutatedMessages() {
            if (!this.thread) return [];

            return this.thread.messages.map((msg) => {
                const message = structuredClone(msg);

                const threadAuthor = this.thread.users.find(
                    (threadUser) => threadUser.user.id == message.author_id
                );

                message.author = this.getThreadUserDisplayUser(threadAuthor);

                let authorNameTokens = [];
                if (message.author?.first_name)
                    authorNameTokens.push(message.author.first_name);
                if (message.author?.last_name)
                    authorNameTokens.push(message.author.last_name);
                message.authorName = authorNameTokens.join(' ');

                message.isTechSupport =
                    this.thread.is_support_thread &&
                    message.author?.is_app_support;

                message.isMine = message.author_id == this.$userStore.user.id;

                message.metaDetails = formatLocalizedDateTime(
                    message.created_at,
                    this.$userStore.effectiveLocale
                );

                return message;
            });
        },
    },

    watch: {
        threadId() {
            this.getMessages();
            this.resetSocket();
        },
    },

    methods: {
        getThreadUserDisplayUser(threadUser) {
            return buildThreadUserDisplayUser(threadUser, this.$employeesStore);
        },
        submitMessage() {
            if (!this.threadSocket)
                return this.$alertStore.pushError(
                    this.$t('messages.alerts.sendError')
                );

            this.threadSocket.send(this.messageInput);
            this.messageInput = '';
        },

        updateSupportPlaceholderToggle() {
            if (
                !this.thread ||
                !this.thread.messages?.length ||
                !this.thread.is_support_thread
            ) {
                return;
            }

            const lastMessage =
                this.thread.messages[this.thread.messages.length - 1];
            const lastMessageIsMe =
                lastMessage.author_id == this.$userStore.user.id;

            const lastMessageTime = moment.utc(lastMessage.created_at);
            const now = moment.utc();
            const diffInMinutes = now.diff(lastMessageTime, 'minutes');
            const lastMessageIsOld = diffInMinutes >= 2;

            this.showSupportPlaceholder =
                !lastMessage.isTechSupport &&
                lastMessageIsMe &&
                lastMessageIsOld;
        },

        triggerFormSubmit() {
            this.$refs.sendButton.click();
        },

        resetSocket() {
            this.closeSocket();

            const socketUrl =
                this.$env.apiRoot +
                'message-thread/watch?thread_id=' +
                this.threadId;
            this.threadSocket = new WebSocket(socketUrl);

            this.threadSocket.onopen = () => {
                console.log('Opened!');
            };

            this.threadSocket.onclose = (e) => {
                console.log('Closed!', e);
            };

            this.threadSocket.onmessage = (event) => {
                try {
                    const response = JSON.parse(event.data);

                    if (response.event == 'new-message') {
                        this.thread.messages.push(response.record);
                        this.$nextTick(() => {
                            this.scrollToLatestMessage();
                        });
                    }
                } catch (e) {
                    console.log(e);
                    this.$alertStore.pushError(
                        this.$t('messages.alerts.retrieveError')
                    );
                }
            };
        },

        closeSocket() {
            if (this.threadSocket) {
                this.threadSocket.close(1000, 'Socket closed by client');
                this.threadSocket = null;
            }
        },

        scrollToLatestMessage() {
            if (this.$refs.messagesContainer) {
                const container = this.$refs.messagesContainer;
                container.scrollTop = container.scrollHeight;
            }
        },

        getMessages() {
            if (this.threadId < 1 || this.fetching) return;

            this.fetching = true;

            const payload = {
                thread_id: this.threadId,
            };

            this.$getData('message-thread/message/read', payload).then(
                (response) => {
                    if (response.success) {
                        this.thread = response.data;
                        this.updateSupportPlaceholderToggle();
                    } else if ('message' in response) {
                        this.$alertStore.pushError(response.message);
                    }

                    this.fetching = false;

                    this.$nextTick(() => {
                        this.scrollToLatestMessage();
                    });
                }
            );
        },

        leaveThread(threadId) {
            this.$postData('message-thread/user/remove', {
                thread_id: threadId,
                user_id: this.$userStore.user.id,
            }).then((response) => {
                if (response?.success) {
                    this.$alertStore.pushSuccess(
                        this.$t('messages.alerts.leaveSuccess')
                    );
                    this.messengerStore.openHistory();
                } else {
                    this.$alertStore.pushError(
                        response?.message ||
                            this.$t('messages.alerts.leaveError')
                    );
                }
            });
        },
    },

    unmounted() {
        this.closeSocket();

        if (this.intervalSupportCheck) {
            clearInterval(this.intervalSupportCheck);
        }
    },

    mounted() {
        this.getMessages();
        this.resetSocket();
        this.$employeesStore.sync();

        // Check every 2 seconds whether we're waiting for a support response
        this.intervalSupportCheck = setInterval(
            this.updateSupportPlaceholderToggle,
            5000
        );
    },

    created() {
        this.messengerStore = useMessengerStore();
    },
};
</script>

<style scoped>
.panel-contents {
    display: inline-flex;
    flex-direction: column;
    gap: var(--space-6);
    height: 100%;
    width: 100%;
}

.thread-messages-container {
    max-height: 100%;
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
    flex-grow: 1;
    overflow-y: auto;
    padding-right: 0.5rem;
}

.thread-messages-container::-webkit-scrollbar {
    width: 3px;
    opacity: 0;
    transition: all 0.3s ease;
}

.thread-messages-container:hover::-webkit-scrollbar {
    opacity: 1;
}

.thread-messages-container::-webkit-scrollbar-track {
    background: var(--neutral-450);
    border-radius: var(--radius-sm);
}

.thread-messages-container::-webkit-scrollbar-thumb {
    background: var(--neutral-600);
    border-radius: var(--radius-sm);
}

.thread-messages-container::-webkit-scrollbar-thumb:hover {
    background: var(--neutral-700);
}

.message-row {
    display: flex;
    gap: var(--space-2);
}

.message {
    padding: var(--space-4);
    display: flex;
    flex-direction: column;
    gap: var(--space-3);
    flex-grow: 1;
    border-radius: var(--radius-default);
}

.outgoing-message {
    flex-direction: row-reverse;
}

.outgoing-message .message {
    background-color: var(--primary-50);
}

.incoming-message .message {
    background-color: var(--primary-25);
}

.message-body {
    overflow-wrap: break-word;
    word-break: break-word;
    display: flex;
    flex-direction: column;
    gap: var(--space-3);
}

.message-body .time-details {
    color: var(--neutral-900);
    font-size: 0.875rem;
    font-weight: 400;
}

hr {
    margin: 0;
    color: var(--neutral-600);
}

.support-text {
    color: var(--primary-300);
    font-weight: bold;
}
</style>
